import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, AUTH_EXPIRED_EVENT, getToken, setToken } from '@/utils/api';
import { AuthContext, type AuthContextValue, type SessionUser } from './AuthContext';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [status, setStatus] = useState<AuthContextValue['status']>(() =>
    getToken() ? 'loading' : 'signedOut',
  );

  // Restore the session from a saved token.
  useEffect(() => {
    if (!getToken()) return;
    api<SessionUser>('/me')
      .then((me) => {
        setUser(me);
        setStatus('signedIn');
      })
      .catch(() => {
        setToken(null);
        setStatus('signedOut');
      });
  }, []);

  // Any request rejected with 401 (e.g. expired token) signs the user out.
  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      setStatus('signedOut');
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await api<{ token: string; user: SessionUser }>('/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setToken(result.token);
    setUser(result.user);
    setStatus('signedIn');
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setStatus('signedOut');
  }, []);

  const updateUser = useCallback((next: SessionUser) => setUser(next), []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, login, logout, updateUser }),
    [status, user, login, logout, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
